import { Result } from '@cp/shared/result';
import { ClientRegistrationState } from '@common/model/client/registration/registrationState';
import { ClientProfileViewModel } from '@common/model/client';
import { useCallback } from 'react';
import { coreCurrentClientApi } from '@cp/entities/client';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { ClientType } from '@common/model/client/clientType';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { useRouter } from '@cp/shared/router';
import { useInjection } from '@cp/shared/di';
import { stepToLayout } from '../ui/stepToComponent';
import { GET_CLIENT_SELECTIONS } from '../di';
import { useRegistrationModal } from './useRegistrationModal';
import { useRegistrationService } from './useRegistrationService';

export const useRegistrationFeature = (): ((
  s: string,
  state: Partial<ClientRegistrationState>,
) => Promise<Result<ClientProfileViewModel, Error>>) => {
  const service = useRegistrationService();
  const openStepModal = useRegistrationModal();
  const router = useRouter();
  const resolver = useClientWebResolver();
  const getClientSelections = useInjection(GET_CLIENT_SELECTIONS);
  const [getProfile] = coreCurrentClientApi.endpoints.getProfile.useLazyQuery();

  return useCallback(
    async (sessionId: string, initialState: Partial<ClientRegistrationState>) => {
      console.debug('Start registration feature');
      const selections = await getClientSelections();
      const state = { ...selections, ...initialState };
      service.init(sessionId, state); // ignore promise for background loading
      if (state.clientType === ClientType.Specialist) {
        await router.navigate(
          resolver.resolve({
            page: ClientWebPage.SignupProvider,
            queryParams: { careerId: state.mainCareerId, sessionId },
          }),
        );
      }
      await service.init(sessionId, state);
      let step = service.getStep();
      while (!step.done && step.value.step !== null) {
        const stepValue = await openStepModal(step.value.step, stepToLayout[step.value.step.name]);
        await service.completeStep(stepValue);
        step = service.getStep();
      }
      return Result.fromAsync(() => getProfile().unwrap());
    },
    [service, openStepModal, getProfile, router, resolver, getClientSelections],
  );
};
